/**
 * Kalshi-lag value signal — "Polymarket and Unabated moved; Kalshi hasn't
 * caught up yet." Fires while Kalshi's executable ask undercuts BOTH the
 * Unabated fair value and Polymarket's ask by ≥2¢ on the same side. By
 * nature FAST-LIVED: it exists in the window between the market moving and
 * Kalshi's book repricing, and dies as soon as the lag closes (Dan,
 * 2026-07-07).
 *
 * Staging roadmap (build against this, not just the current consumer):
 *   1. TODAY — inline cue: the dashboard pill's price font pulses gold
 *      (PricePill `valueSignal`, animate-value-gold-text-pulse).
 *   2. NEXT — user-optable notification/alert: needs render-independent
 *      onset/offset detection with hysteresis. Reuse the divergence alert's
 *      pattern next door (stepDivergenceLatch in polyKalshiDivergence.ts:
 *      silent first-sight seeding, edge-triggered fire, re-arm floor,
 *      stale-can't-disarm) rather than inventing a second latch.
 *   3. LATER — bot auto-buy on the signal. At that stage the freshness
 *      gates below stop being cosmetic and become the safety interlock.
 *
 * Freshness is load-bearing at every stage. A frozen poly feed keeps
 * rendering its last ask with no visible "dead" marker (CLAUDE.md "Stale
 * prices mask dead hydra feeds") — Kalshi then looks 2¢ cheap forever, and
 * "Kalshi lags" computed from a dead reference is precisely the false
 * positive that would spam alerts or burn an auto-buy. Both references must
 * be fresh: the poly ORDERBOOK age (the ask is derived from the ladder) and
 * the consensus frame age.
 *
 * Pure + view-agnostic on purpose: consumers hand in prices/ages; nothing
 * here touches marketStore or React. Display surfaces AND future
 * notification/bot layers must share this predicate so "the signal" never
 * forks into subtly different definitions.
 */

import { POLY_STALE_MS } from './polyKalshiDivergence';

/** Minimum edge (¢) Kalshi's ask must undercut BOTH references by. */
export const VALUE_LAG_EDGE_CENTS = 2;

/** Consensus frame older than this is too stale to anchor the signal.
 *  Matches the Odds column's "aged" boundary (its age chip appears at
 *  5 min), so the signal never trusts a consensus the UI is already
 *  flagging as old. */
export const VALUE_LAG_CONSENSUS_STALE_MS = 300_000;

/**
 * The raw price predicate: ask undercuts BOTH the fair value and the rival
 * venue's ask by at least `edgeCents` (all three in the same side's price
 * space; fair at tenth precision when available). Any missing input → false:
 * the signal requires confirmation from both references, never "cheap vs
 * whatever data happens to exist". No freshness — see kalshiLagValueActive.
 */
export function kalshiLagValueEdge(
  askCents: number | null,
  rivalAskCents: number | null,
  fairCents: number | null,
  edgeCents = VALUE_LAG_EDGE_CENTS,
): boolean {
  return (
    askCents != null &&
    rivalAskCents != null &&
    fairCents != null &&
    askCents <= fairCents - edgeCents &&
    askCents <= rivalAskCents - edgeCents
  );
}

export interface KalshiLagInputs {
  /** Kalshi's executable ask (taker top of book) on this side, in ¢. */
  kalshiAskCents: number | null;
  /** Polymarket's executable ask on the same side, in ¢. */
  polyAskCents: number | null;
  /** Unabated fair value in the same side's space, tenth precision when
   *  hydra sends the sub-cent feed. */
  fairCents: number | null;
  /** Poly ORDERBOOK age (marketStore.getOrderbookAgeMs(polyTicker)); null =
   *  no book ever received. The ask is derived from the ladder, so this is
   *  the honest "is that poly price real right now" input. */
  polyBookAgeMs: number | null;
  /** Age of the consensus frame (now − consensus.lastUpdateMs). */
  consensusAgeMs: number | null;
}

/**
 * The full signal: price edge AND both references fresh. This is the ONE
 * definition every stage consumes — pill glow today, notification latch and
 * bot trigger later. Stale or absent poly book / consensus → false, exactly
 * like isDivergenceActive's "an unreliable price must not raise a flag".
 */
export function kalshiLagValueActive(
  inputs: KalshiLagInputs,
  edgeCents = VALUE_LAG_EDGE_CENTS,
): boolean {
  const polyFresh = inputs.polyBookAgeMs != null && inputs.polyBookAgeMs <= POLY_STALE_MS;
  const consensusFresh =
    inputs.consensusAgeMs != null && inputs.consensusAgeMs <= VALUE_LAG_CONSENSUS_STALE_MS;
  if (!polyFresh || !consensusFresh) return false;
  return kalshiLagValueEdge(inputs.kalshiAskCents, inputs.polyAskCents, inputs.fairCents, edgeCents);
}
